import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Input, InputNumber, Modal, Radio, Select, Spin, Switch, Table, Tag, Tooltip, message } from "antd";
import { AlertTriangle, KeyRound, Plus, ShieldOff, Terminal, Users } from "lucide-react";
import { AN_COLORS, AN_FONT, AN_MONO } from "../../../../design-system/tokens";
import { anTableProps, CountChip, MonoCell, PageHeader, RowActionMenu, TableCard, tableEmpty } from "../../../../design-system/tableUi";
import { DOMAIN_ID, ORG_ID, USER_EMAIL } from "../../../../constants";
import { isAdminUser } from "../../../../utils/helpers";
import { getPlatformUserList } from "../../../../service/tenantService";
import {
  createSSHPolicy,
  DEFAULT_SSH_POLICY_FLOW,
  deleteSSHPolicy,
  errorText,
  GRANTS_END_FAILED,
  GROUP_SCOPE_SUPPORTED,
  listSSHPolicy,
  resumeSSHPolicy,
  SSH_CREDENTIAL_TYPE,
  SSH_POLICY_FLOW_BADGE,
  SSH_POLICY_FLOWS,
  suspendSSHPolicy,
  updateSSHPolicy,
} from "../../../../service/sshPolicyService";
import {
  accountNameError,
  getAdminCredentialStatus,
  listEndpointAccounts,
  listGrants,
  listInstances,
  listJumpServer,
  listSSHConnections,
  NO_KEY_ACCOUNTS,
  TEMP_ACCOUNT,
  endpointLifecycle,
} from "../../../../service/sshProxyService";
import { card, CopyButton, FieldLabel, InlineValue, Note, PanelHead } from "../sshProxyUi";
import GrantsPanel from "./GrantsPanel";
import { StepRow } from "../../../../components/privilegedAccessManagement/endpoints/addEndpoint/onboardUi";

// Endpoint Policy (stored as policyType "SSH"): these people may reach these
// accounts on these hosts, with this action. The gateway is the one each host
// (an onboarded endpoint) already belongs to: the form sends no
// ssh.jumpServers, which the backend reads as "no gateway filter" (grants take
// the gateway from the endpoint). Older policies may still carry one.
//
// Saving also issues grants: one key per named account on each named host
// that is an onboarded endpoint with a gateway.
//
// Deny-by-default is live (#338): a session no policy covers is refused, and
// when several match the most specific wins. Only Approved policies match;
// create and update both store Approved.
//
// Used two ways: scoped to one gateway (the gateway's Policy tab, jumpServerId
// set) and across every gateway (PAM → Policies, jumpServerId unset), where the
// gateway becomes a column. `embedded` is the latter as a tab of the Policies
// page: that page draws the header, this a one-line toolbar.

const FLOW_LABEL = SSH_POLICY_FLOW_BADGE;
const FLOW_TAG = { mfa_required: "blue", allow: "gold", block: "red" };

// Allows host or host:port (a non-standard sshd port). No user@, no spaces.
const HOST_RE = /^[A-Za-z0-9._-]+(:\d{1,5})?$/;

// The address an endpoint is named by everywhere on the SSH path: the gateway
// dials it, the policy names it, and its key is issued for it.
const endpointAddress = (r) => r?.privateIp || r?.publicIp || r?.ipAddress || "";

const flowOf = (row) => row?.policyJson?.policyFlow || "";

// The onboarded endpoint a policy host names, the way the backend matches it
// when issuing keys: private IP, public IP or hostname, ignoring any port.
const endpointForHost = (instances, host) => {
  const h = String(host || "").split(":")[0].toLowerCase();
  if (!h) return null;
  return (
    instances.find((r) =>
      [r.privateIp, r.publicIp, r.hostName].some((v) => v && String(v).toLowerCase() === h),
    ) || null
  );
};

const isNoKeyAccount = (a) => NO_KEY_ACCOUNTS.includes(String(a || "").toLowerCase());

// "temp" is not a real account: the session gets an account of its own,
// created on the endpoint for it and deleted when it ends. Offered as the last
// account option.
const TEMP_LABEL = "temp — a temporary account per session";
const accountLabel = (a) => (a === TEMP_ACCOUNT ? "Temporary user" : a);

// "Oct 15, 2026 10:14", local time, on one line.
const fmtModified = (dt) => {
  const d = dt ? new Date(dt) : null;
  if (!d || Number.isNaN(d.getTime())) return "—";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// The Local Users page's tag style, for the endpoint's admin account.
const ADMIN_BADGE = {
  display: "inline-block", fontFamily: AN_MONO, fontSize: 10.5, letterSpacing: 0.4, textTransform: "uppercase",
  padding: "1px 6px", borderRadius: 4, whiteSpace: "nowrap", background: AN_COLORS.warnBg, color: AN_COLORS.warnFg,
};

// ─── Sudo ───────────────────────────────────────────────────────────────────
// ssh.sudo elevates sessions this policy lets in, when they were approved with
// MFA: full sudo, or named commands, for at most maxMinutes. These mirror the
// backend's checks (sudogrants.NormalisePolicySudo / ValidCommand) and its
// messages, so the form says what a save would answer.
const SUDO_DEFAULT_MINUTES = 60;
const SUDO_MIN_MINUTES = 5;
const SUDO_MAX_MINUTES = 720;
const SUDO_MAX_COMMANDS = 32;
const SUDO_MAX_COMMAND_LEN = 256;
const SUDO_COMMAND_CHARS = /^[A-Za-z0-9/._+@%" -]+$/;
const SUDO_BAD_COMMAND = 'a command must be an absolute path, optionally with arguments, using only letters, digits, spaces and / . _ + @ % " -';
const SUDO_NO_SCOPE = "name at least one command, or grant full sudo";
const SUDO_BAD_DURATION = `the maximum elevation must be between ${SUDO_MIN_MINUTES} and ${SUDO_MAX_MINUTES} minutes`;
const SUDO_ERROR_PREFIX = "ssh.sudo:";
const SUDO_MODES = [
  { value: "none", label: "None" },
  { value: "full", label: "Full sudo" },
  { value: "commands", label: "Specific commands" },
];

const validSudoCommand = (c) =>
  c.length >= 2 && c.length <= SUDO_MAX_COMMAND_LEN && c[0] === "/" && SUDO_COMMAND_CHARS.test(c) && !c.includes("  ");

// One command per line: trimmed, blank lines dropped, duplicates removed.
const parseSudoCommands = (text) => [...new Set(String(text || "").split("\n").map((l) => l.trim()).filter(Boolean))];

const sentence = (t) => (t ? t.charAt(0).toUpperCase() + t.slice(1) : t);

const sudoError = (f) => {
  if (f.sudoMode === "none") return null;
  if (f.sudoMode === "commands") {
    const cmds = parseSudoCommands(f.sudoCommands);
    const bad = cmds.find((c) => !validSudoCommand(c));
    if (bad) return sentence(`${SUDO_BAD_COMMAND}: ${JSON.stringify(bad)}`);
    if (cmds.length > SUDO_MAX_COMMANDS) return `At most ${SUDO_MAX_COMMANDS} commands`;
    if (!cmds.length) return sentence(SUDO_NO_SCOPE);
  }
  const m = f.sudoMinutes;
  if (!Number.isInteger(m) || m < SUDO_MIN_MINUTES || m > SUDO_MAX_MINUTES) return sentence(SUDO_BAD_DURATION);
  return null;
};

// ssh.sudo as the backend stores it, or undefined for none (the key is omitted).
const buildSudo = (f) => {
  if (f.sudoMode === "full") return { full: true, maxMinutes: f.sudoMinutes };
  if (f.sudoMode === "commands") return { commands: parseSudoCommands(f.sudoCommands), maxMinutes: f.sudoMinutes };
  return undefined;
};

// A stored ssh.sudo back into the form's three fields.
const sudoFormFields = (sudo) => {
  const minutes = Number(sudo?.maxMinutes) || SUDO_DEFAULT_MINUTES;
  if (sudo?.full) return { sudoMode: "full", sudoCommands: "", sudoMinutes: minutes };
  if ((sudo?.commands || []).length) return { sudoMode: "commands", sudoCommands: sudo.commands.join("\n"), sudoMinutes: minutes };
  return { sudoMode: "none", sudoCommands: "", sudoMinutes: SUDO_DEFAULT_MINUTES };
};

const emptyForm = (presetHost) => ({
  id: null,
  row: null,
  policyName: "",
  nameTouched: false,
  users: [],
  matchAll: false,
  hosts: presetHost ? [presetHost] : [],
  accounts: [],
  policyFlow: DEFAULT_SSH_POLICY_FLOW,
  ...sudoFormFields(null),
  sudoServerError: null, // the backend's 400 about ssh.sudo, prefix stripped
});

const suggestedName = (f) => {
  const host = f.hosts[0] ? f.hosts[0] + (f.hosts.length > 1 ? ` +${f.hosts.length - 1}` : "") : "any host";
  const account = f.accounts[0] ? f.accounts[0] + (f.accounts.length > 1 ? ` +${f.accounts.length - 1}` : "") : "any account";
  const who = f.matchAll ? "everyone" : `${f.users.length} user${f.users.length === 1 ? "" : "s"}`;
  return `${host} ${account} — ${who}`;
};

export default function SshPolicyManager({ jumpServerId, presetHost, embedded }) {
  const [messageApi, msgCtx] = message.useMessage();
  const [modal, modalCtx] = Modal.useModal();
  const scoped = !!jumpServerId;

  // Every Endpoint Policy (null while loading). Scoped to a gateway, the list
  // shows those on it — see policyGateways — but naming the policy that holds
  // a grant needs them all: it can be a policy on another gateway.
  const [allPolicies, setAllPolicies] = useState(null);
  const [users, setUsers] = useState([]); // [{ userId, email }]
  const [gateways, setGateways] = useState([]);
  const [instances, setInstances] = useState([]);
  const [instancesLoaded, setInstancesLoaded] = useState(false);

  const [form, setForm] = useState(() => {
    if (!presetHost) return null;
    const f = emptyForm(presetHost);
    return { ...f, policyName: suggestedName(f) };
  });
  const [saving, setSaving] = useState(false);
  const [conflicts, setConflicts] = useState(null);
  const [granted, setGranted] = useState(null); // what the last save granted
  const [savedPolicyId, setSavedPolicyId] = useState(null); // grants panel after a save
  const [expanded, setExpanded] = useState([]); // policy rows showing their grants

  const orgId = ORG_ID();
  const tenantId = DOMAIN_ID();

  const load = useCallback(() => {
    setAllPolicies(null);
    listSSHPolicy({ orgId, tenantId, onSuccess: (rows) => setAllPolicies(rows), onFailure: () => setAllPolicies([]) });
  }, [orgId, tenantId]);

  useEffect(() => {
    load();
    getPlatformUserList({
      payload: { page: 1, pageSize: 500 },
      onSuccess: (res) => setUsers((res?.data || []).filter((u) => u.email).map((u) => ({ userId: u.userId, email: u.email }))),
      onFailure: () => {},
    });
    listJumpServer({ payload: { pageSize: 200 }, onSuccess: (d) => setGateways(d?.jumpServers || []), onFailure: () => {} });
    listInstances({
      onSuccess: (rows) => {
        setInstances(rows);
        setInstancesLoaded(true);
      },
      onFailure: () => setInstancesLoaded(true),
    });
  }, [load]);

  // The gateways a policy applies through: those it names (older policies
  // carry ssh.jumpServers, and the backend then filters on them), else the
  // gateways of the endpoints its hosts name.
  const policyGateways = useCallback(
    (row) => {
      const ssh = row?.policyJson?.ssh || {};
      const named = (ssh.jumpServers || []).map(Number).filter(Boolean);
      if (named.length) return [...new Set(named)];
      const ids = (ssh.hosts || []).map((h) => Number(endpointForHost(instances, h)?.jumpServerId)).filter(Boolean);
      return [...new Set(ids)];
    },
    [instances],
  );

  // Scoped to a gateway, its rows wait for the endpoints, which say which
  // gateway each host is on.
  const policies = useMemo(() => {
    if (!allPolicies || (scoped && !instancesLoaded)) return null;
    if (!scoped) return allPolicies;
    return allPolicies.filter((r) => policyGateways(r).includes(Number(jumpServerId)));
  }, [allPolicies, scoped, instancesLoaded, policyGateways, jumpServerId]);

  const gatewayName = useCallback(
    (id) => {
      const g = gateways.find((x) => String(x.id) === String(id));
      return g?.serverName || `Gateway #${id}`;
    },
    [gateways],
  );

  const endpointName = useCallback(
    (id) => {
      const r = instances.find((x) => String(x.instanceId) === String(id));
      if (!r) return `Endpoint #${id}`;
      const address = endpointAddress(r);
      return r.hostName && r.hostName !== address ? `${r.hostName} (${address})` : address || r.hostName;
    },
    [instances],
  );
  const policyNameById = useCallback(
    (id) =>
      (allPolicies || []).find((p) => p.id === id)?.policyName || (id ? `${String(id).slice(0, 8)}…` : "another policy"),
    [allPolicies],
  );

  const userOptions = users.map((u) => ({ value: u.email, label: u.email }));

  // ─── Form helpers ─────────────────────────────────────────────────────────
  const patch = (changes) =>
    setForm((f) => {
      const next = { ...f, ...changes };
      if (!next.nameTouched) next.policyName = suggestedName(next);
      return next;
    });

  const openCreate = () => {
    setConflicts(null);
    setGranted(null);
    const f = emptyForm();
    setForm({ ...f, policyName: suggestedName(f) });
  };

  const openEdit = (row) => {
    const ssh = row?.policyJson?.ssh || {};
    setConflicts(null);
    setGranted(null);
    setForm({
      id: row.id,
      row,
      policyName: row.policyName || "",
      nameTouched: true,
      users: ssh.users || [],
      matchAll: !!ssh.matchAll,
      hosts: ssh.hosts || [],
      accounts: ssh.accounts || [],
      // A stored policy with no policyFlow is enforced as allow; show that
      // rather than silently tightening it to MFA on the next save.
      policyFlow: flowOf(row) || "allow",
      ...sudoFormFields(ssh.sudo),
      sudoServerError: null,
    });
  };

  // Hosts come from onboarded endpoints that have a gateway. Scoped to one
  // gateway, only its endpoints; across gateways, all of them, each labelled
  // with the gateway it is reached through.
  const hostOptions = useMemo(
    () =>
      instances
        // A decommissioned endpoint has no access to grant.
        .filter((r) => r.jumpServerId && !endpointLifecycle(r.status) && (!scoped || String(r.jumpServerId) === String(jumpServerId)))
        .map((r) => {
          const address = endpointAddress(r);
          const name = r.hostName && r.hostName !== address ? `${r.hostName} (${address})` : address;
          return { value: address, label: `${name} · via ${gatewayName(r.jumpServerId)}`, row: r };
        })
        .filter((o) => o.value),
    [instances, scoped, jumpServerId, gatewayName],
  );

  const errors = form
    ? {
        policyName: !form.policyName.trim() ? "Enter a name" : null,
        users: !form.matchAll && form.users.length === 0 ? "Pick at least one user, or choose everyone" : null,
        // At least one host: grants are issued only for the hosts picked.
        hosts: !form.hosts.length
          ? "Pick at least one host"
          : form.hosts.find((h) => !HOST_RE.test(h))
            ? `"${form.hosts.find((h) => !HOST_RE.test(h))}" isn't a host or host:port`
            : null,
        // At least one account: an empty list grants nothing.
        accounts:
          form.accounts.length === 0
            ? "Add at least one account — an empty list grants nothing"
            : form.accounts.map(accountNameError).find(Boolean) || null,
        sudo: sudoError(form),
      }
    : {};
  const hasErrors = Object.values(errors).some(Boolean);

  // ─── Save ─────────────────────────────────────────────────────────────────
  const buildPolicyJson = (f) => {
    const sudo = buildSudo(f);
    return {
    // Edit keeps every field the stored policy had; the SSH block, the name
    // and the action are what the form owns.
    ...(f.row?.policyJson || {}),
    policyType: SSH_CREDENTIAL_TYPE,
    policyName: f.policyName.trim(),
    // Always explicit. Update stores policyJson as given, so an edit without
    // policyFlow would quietly become allow.
    policyFlow: f.policyFlow || DEFAULT_SSH_POLICY_FLOW,
    ssh: {
      users: f.matchAll ? [] : f.users,
      groups: [],
      matchAll: !!f.matchAll,
      hosts: f.hosts,
      accounts: f.accounts,
      // No jumpServers: each host's endpoint already has its gateway, and the
      // backend reads an absent list as no gateway filter. Editing a policy
      // that had one drops it.
      // Omitted for none: the backend reads an absent block as no sudo.
      ...(sudo ? { sudo } : {}),
    },
    };
  };

  const showGranted = (f) => {
    // With no jumpServers the backend lists each host through every gateway;
    // only the endpoint's own gateway reaches it.
    const relevant = (c) =>
      Number(c.jumpServerId) === Number(endpointForHost(instances, c.host)?.jumpServerId) &&
      // Empty hosts or accounts mean "any".
      (!f.hosts.length || f.hosts.some((h) => h === c.host || h.split(":")[0] === c.host)) &&
      (!f.accounts.length || f.accounts.includes(c.account));
    // For everyone, one lookup for the signed-in admin (userId 0 = me) shows
    // the command format every operator gets.
    const targets = f.matchAll
      ? [{ userId: 0, email: "Every user in the organisation" }]
      : f.users.map((email) => ({ email, userId: users.find((u) => u.email === email)?.userId }));
    setGranted({ flow: f.policyFlow, matchAll: f.matchAll, rows: targets.map((t) => ({ ...t, loading: true })) });
    targets.forEach((t, i) => {
      const done = (entry) =>
        setGranted((g) => (g ? { ...g, rows: g.rows.map((r, j) => (j === i ? { ...r, ...entry, loading: false } : r)) } : g));
      if (t.userId === undefined) return done({ error: "No platform user with this email." });
      listSSHConnections({
        userId: t.userId,
        onSuccess: (data) =>
          done({ connections: (data.connections || []).filter(relevant), message: data.message }),
        onFailure: (err) => done({ error: err?.response?.data?.error || "Couldn't load connections." }),
      });
    });
  };

  const doSave = () => {
    const f = form;
    setSaving(true);
    setConflicts(null);
    const policyJson = buildPolicyJson(f);
    // Save returns only the policy id (create: data, update: updatedPolicyID);
    // its grants are issued after, so the grants panel polls for a few seconds.
    const onDone = (res) => {
      setSaving(false);
      setForm(null);
      messageApi.success(f.id ? "Endpoint Policy updated" : "Endpoint Policy created");
      showGranted(f);
      const id = res?.updatedPolicyID || (typeof res?.data === "string" ? res.data : null);
      // The backend answers the nil UUID when nothing was created.
      setSavedPolicyId(id && !/^0{8}-0{4}-0{4}-0{4}-0{12}$/.test(id) ? id : null);
      load();
    };
    const onFailure = (res) => {
      setSaving(false);
      const text = String(res?.data?.error || res?.data?.message || res?.message || "");
      // A 400 about ssh.sudo goes under the Sudo field; anything else stays a toast.
      if (text.startsWith(SUDO_ERROR_PREFIX)) {
        setForm((cur) => (cur ? { ...cur, sudoServerError: sentence(text.slice(SUDO_ERROR_PREFIX.length).trim()) } : cur));
        return;
      }
      messageApi.error(text || "Couldn't save the policy");
    };
    if (f.id) {
      updateSSHPolicy({
        payload: { id: f.id, orgId, tenantId, policyName: f.policyName.trim(), policyJson, updatedBy: USER_EMAIL() || "" },
        onSuccess: onDone,
        onFailure,
      });
    } else {
      createSSHPolicy({
        payload: { orgId, tenantId, policyName: f.policyName.trim(), policyJson },
        onSuccess: onDone,
        onFailure,
        onConflict: ({ conflicts: c, warning }) => {
          setSaving(false);
          setConflicts({ rows: c, warning });
        },
      });
    }
  };

  // Only one grant per account per host can exist: saving a policy for an
  // account another policy already holds on a host silently moves that grant
  // here. Check the picked endpoints first and say so before saving.
  const save = async () => {
    const f = form;
    const picked = [...new Map(f.hosts.map((h) => endpointForHost(instances, h)).filter(Boolean).map((r) => [r.instanceId, r])).values()];
    if (!picked.length || !f.accounts.length) return doSave();
    setSaving(true);
    const lists = await Promise.all(
      picked.map(
        (r) =>
          new Promise((resolve) =>
            listGrants({ instanceId: r.instanceId, onSuccess: (g) => resolve(g.map((x) => ({ ...x, endpoint: r }))), onFailure: () => resolve([]) }),
          ),
      ),
    );
    setSaving(false);
    const ownIds = [f.id, f.row?.id].filter(Boolean);
    const taken = lists
      .flat()
      .filter((g) => (g.status === "active" || g.status === "pending") && f.accounts.includes(g.account) && !ownIds.includes(g.policyId));
    if (!taken.length) return doSave();
    modal.confirm({
      title: "Another policy holds a grant for this account",
      width: 560,
      content: (
        <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
          {taken.map((g) => (
            <li key={g.id}>
              <InlineValue>{g.account}</InlineValue> on <strong>{g.endpoint.hostName || endpointAddress(g.endpoint)}</strong> is held by policy{" "}
              <strong>{policyNameById(g.policyId)}</strong>. Saving moves it here.
            </li>
          ))}
        </ul>
      ),
      okText: "Continue",
      cancelText: "Cancel",
      onOk: doSave,
    });
  };

  // Delete and suspend end the policy's grants first. If that fails, the
  // request fails, the policy is left as it was, and the admin can retry.
  const offerRetry = (verb, row, err, retry) => {
    const msg = errorText(err);
    const keysFailed = msg.toLowerCase().includes(GRANTS_END_FAILED);
    modal.confirm({
      title: keysFailed ? `Couldn't ${verb} "${row.policyName || "this policy"}": its grants couldn't be ended` : `Couldn't ${verb} the policy`,
      content: keysFailed
        ? "Nothing was changed — the policy and its grants are as they were. Try again."
        : msg || "Try again.",
      okText: "Retry",
      onOk: retry,
    });
  };

  const runDelete = (row) =>
    new Promise((resolve) =>
      deleteSSHPolicy({
        orgId,
        tenantId,
        policyId: row.id,
        onSuccess: () => {
          messageApi.success("Endpoint Policy deleted");
          load();
          resolve();
        },
        onFailure: (err) => {
          resolve();
          offerRetry("delete", row, err, () => runDelete(row));
        },
      }),
    );

  // What delete does, as the backend does it (DeletePolicyJSON): every
  // pending or active grant is ended in the same request, before the row goes
  // — marked ended, its key retired from the vault, a removal job queued for
  // the gateway — and the sudo the policy gave live sessions is ended too.
  // Open SSH sessions are not disconnected. If ending fails, nothing is deleted.
  const deleteContent = (count) => (
    <div style={{ display: "grid", gap: 8, fontSize: 13, lineHeight: 1.55 }}>
      <div>
        {count === null
          ? "Its active grants end immediately"
          : count === 0
            ? "It has no active grants. Any it had end immediately"
            : `Its ${count} active grant${count === 1 ? "" : "s"} end${count === 1 ? "s" : ""} immediately`}
        : each key is retired from the vault, so no new session can use it, and the gateway then removes it from the endpoint.
      </div>
      <div>
        Sessions already open are not disconnected, but any sudo this policy gave them is removed.
      </div>
      <div>
        New sessions it allowed are refused unless another policy covers them. If the grants can't be ended, nothing is
        deleted.
      </div>
    </div>
  );

  const confirmDelete = (row) => {
    const dialog = modal.confirm({
      title: `Delete "${row.policyName || "this policy"}"?`,
      width: 520,
      content: deleteContent(null),
      okText: "Delete policy",
      okButtonProps: { danger: true },
      onOk: () => runDelete(row),
    });
    listGrants({
      policyId: row.id,
      onSuccess: (rows) =>
        dialog.update({ content: deleteContent((rows || []).filter((g) => g.status === "active" || g.status === "pending").length) }),
      onFailure: () => {},
    });
  };

  const runSuspend = (row) =>
    new Promise((resolve) =>
      suspendSSHPolicy({
        orgId,
        tenantId,
        policyId: row.id,
        onSuccess: () => {
          messageApi.success("Endpoint Policy suspended");
          load();
          resolve();
        },
        onFailure: (err) => {
          resolve();
          offerRetry("suspend", row, err, () => runSuspend(row));
        },
      }),
    );

  const confirmSuspend = (row) =>
    modal.confirm({
      title: `Suspend "${row.policyName || "this policy"}"?`,
      content: "It stops matching sessions and its grants end. Resume it to issue them again.",
      okText: "Suspend",
      onOk: () => runSuspend(row),
    });

  const resume = (row) =>
    resumeSSHPolicy({
      orgId,
      tenantId,
      policyId: row.id,
      onSuccess: () => {
        messageApi.success("Endpoint Policy resumed — its grants are being issued");
        load();
        setExpanded((k) => (k.includes(row.id) ? k : [...k, row.id]));
      },
      onFailure: (err) => offerRetry("resume", row, err, () => resume(row)),
    });

  const toggleKeys = (row) => setExpanded((k) => (k.includes(row.id) ? k.filter((x) => x !== row.id) : [...k, row.id]));

  // ─── List ─────────────────────────────────────────────────────────────────
  const columns = [
    { title: "Name", key: "name", render: (_, r) => <span style={{ display: "inline-block", minWidth: 180, fontWeight: 500, color: AN_COLORS.ink }}>{r.policyName || "—"}</span> },
    ...(scoped
      ? []
      : [
          {
            // The gateways its hosts are reached through (or, for an older
            // policy, the ones it names).
            title: "Gateway",
            key: "gateway",
            render: (_, r) => policyGateways(r).map((id) => gatewayName(id)).join(", ") || <span style={{ color: AN_COLORS.muted }}>—</span>,
          },
        ]),
    {
      title: "Who",
      key: "who",
      render: (_, r) => {
        const ssh = r.policyJson?.ssh || {};
        if (ssh.matchAll) return <Tag>Everyone</Tag>;
        const list = ssh.users || [];
        return list.length ? <span style={{ fontSize: 12.5 }}>{list.join(", ")}</span> : <span style={{ color: AN_COLORS.muted }}>Nobody</span>;
      },
    },
    { title: "Hosts", key: "hosts", render: (_, r) => <MonoCell>{(r.policyJson?.ssh?.hosts || []).join(", ") || "Any host"}</MonoCell> },
    {
      title: "Accounts",
      key: "accounts",
      render: (_, r) => {
        const ssh = r.policyJson?.ssh || {};
        const nCmds = (ssh.sudo?.commands || []).length;
        const sudoTag = ssh.sudo?.full ? "sudo" : nCmds ? `sudo: ${nCmds} cmd${nCmds === 1 ? "" : "s"}` : null;
        const accounts = <MonoCell>{(ssh.accounts || []).map(accountLabel).join(", ") || "Any account"}</MonoCell>;
        if (!sudoTag) return accounts;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
            <div style={{ minWidth: 0 }}>{accounts}</div>
            <Tooltip title={ssh.sudo?.full ? "Full sudo for MFA-approved sessions" : (ssh.sudo.commands || []).join(", ")}>
              <Tag style={{ marginInlineEnd: 0, flexShrink: 0 }}>{sudoTag}</Tag>
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: 150,
      render: (_, r) => {
        const f = flowOf(r);
        return f ? <Tag color={FLOW_TAG[f]}>{FLOW_LABEL[f] || f}</Tag> : <Tag color="gold">Allow (no action set)</Tag>;
      },
    },
    {
      title: "Status",
      key: "status",
      width: 110,
      render: (_, r) => {
        const st = String(r.status || "").toLowerCase();
        return st === "suspended" ? <Tag>Suspended</Tag> : st === "approved" ? <Tag color="green">Active</Tag> : <Tag>{r.status || "—"}</Tag>;
      },
    },
    {
      title: "Last modified",
      key: "updated",
      width: 140,
      render: (_, r) => <span style={{ fontSize: 12, whiteSpace: "nowrap" }}>{fmtModified(r.updatedAt || r.createdAt)}</span>,
    },
    {
      title: "",
      key: "row-actions",
      width: 56,
      render: (_, r) => (
        <RowActionMenu
          items={[
            { key: "grants", label: expanded.includes(r.id) ? "Hide active grants" : "Active grants", onClick: () => toggleKeys(r) },
            { key: "edit", label: "Edit", onClick: () => openEdit(r) },
            String(r.status || "").toLowerCase() === "suspended"
              ? { key: "resume", label: "Resume", onClick: () => resume(r) }
              : { key: "suspend", label: "Suspend", onClick: () => confirmSuspend(r) },
            { key: "delete", label: "Delete", danger: true, onClick: () => confirmDelete(r) },
          ]}
        />
      ),
    },
  ];

  const none = policies !== null && policies.length === 0;

  const newPolicyButton = (
    <Button type="primary" icon={<Plus size={13} strokeWidth={2.2} />} onClick={openCreate}>
      New policy
    </Button>
  );
  const noPolicyNote = (
    <Note tone="warn" icon={<ShieldOff size={15} />}>
      <strong>No policy — every session {scoped ? "through this gateway " : ""}is refused.</strong>
    </Note>
  );
  const policyTable = (
        <Table
          {...anTableProps}
          rowKey={(r) => r.id}
          columns={columns}
          dataSource={policies || []}
          loading={policies === null}
          pagination={false}
          locale={tableEmpty(policies === null, "No Endpoint Policies.")}
          scroll={{ x: true }}
          expandable={{
            expandedRowKeys: expanded,
            onExpand: (_, r) => toggleKeys(r),
            expandIcon: ({ expanded: open, onExpand, record }) => (
              <Tooltip title={open ? "Hide active grants" : "Active grants"}>
                <button
                  type="button"
                  onClick={(e) => onExpand(record, e)}
                  aria-label={open ? "Hide active grants" : "Active grants"}
                  style={{ border: "none", background: "none", padding: 2, cursor: "pointer", color: open ? AN_COLORS.primary : AN_COLORS.muted }}
                >
                  <KeyRound size={14} />
                </button>
              </Tooltip>
            ),
            expandedRowRender: (r) => (
              <div style={{ padding: "4px 4px 8px" }}>
                <GrantsPanel policyId={r.id} endpointName={endpointName} canSync compact />
              </div>
            ),
          }}
        />
  );

  return (
    // minmax(0, 1fr): let the table scroll inside its card instead of its
    // min-content width stretching the whole tab past the page.
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 16 }}>
      {msgCtx}
      {modalCtx}

      {scoped ? (
        // A tab inside the gateway page: a panel, with the same primary
        // button the Gateways and Endpoints pages use.
        <div style={{ ...card, padding: 18 }}>
          <PanelHead
            eyebrow="Policy"
            title="Endpoint Policies on this gateway"
            desc="Who may reach which accounts on which hosts, and whether they need approval. A session no policy covers is refused; deny always wins."
            right={newPolicyButton}
          />
          {none ? noPolicyNote : policyTable}
        </div>
      ) : (
        // PAM → Policies: the same page shape as Gateways and Endpoints — a
        // header with a count and the primary action, then the table card.
        <>
          {embedded ? (
            // A tab of the Policies page, which draws the page header: the
            // count, what the list is, and New policy on one line.
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                {policies && <CountChip count={policies.length} label={policies.length === 1 ? "Policy" : "Policies"} />}
                <span style={{ fontSize: 13, color: AN_COLORS.muted }}>
                  Deny-by-default: a session no policy covers is refused.
                </span>
              </div>
              {newPolicyButton}
            </div>
          ) : (
            <PageHeader
              eyebrow="PAM · Policies"
              title="Endpoint Policies"
              subtitle="Who may reach which accounts on which endpoints. Deny-by-default: a session no policy covers is refused."
              chips={policies && <CountChip count={policies.length} label={policies.length === 1 ? "Policy" : "Policies"} />}
              actions={newPolicyButton}
            />
          )}
          {none && noPolicyNote}
          <TableCard>{policyTable}</TableCard>
        </>
      )}

      {/* New / edit opens as a form dialog, the same shape as Onboard a
          Gateway and Add Endpoint. */}
      <Modal
        open={!!form}
        onCancel={() => { setForm(null); setConflicts(null); }}
        footer={null}
        width={900}
        destroyOnClose
        title={form?.id ? "Edit Endpoint Policy" : "New Endpoint Policy"}
        styles={{ body: { maxHeight: "70vh", overflowY: "auto", paddingTop: 8 } }}
      >
        {conflicts && (
        <Note tone="danger" icon={<AlertTriangle size={15} />}>
          <strong>Not saved — this overlaps an existing policy.</strong> {conflicts.warning}
          <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
            {(conflicts.rows || []).map((c) => (
              <li key={c.id}>{c.policyName || c.id}</li>
            ))}
          </ul>
        </Note>
      )}

        {form && (
          <PolicyForm
            form={form}
            errors={errors}
            hasErrors={hasErrors}
            saving={saving}
            scoped={scoped}
            patch={patch}
            setForm={setForm}
            userOptions={userOptions}
            hostOptions={hostOptions}
            instances={instances}
            gateways={gateways}
            onCancel={() => { setForm(null); setConflicts(null); }}
            onSave={save}
          />
        )}
      </Modal>

      {granted && <GrantedPanel granted={granted} onClose={() => setGranted(null)} />}

      {savedPolicyId && (
        <div style={{ ...card, padding: 18 }}>
          <PanelHead
            eyebrow="Grants"
            title="Grants issued by this save"
            desc="Grant keys are installed on the endpoints after the policy saves, so this refreshes on its own for a few seconds."
            right={<Button size="small" onClick={() => setSavedPolicyId(null)}>Close</Button>}
          />
          <GrantsPanel policyId={savedPolicyId} endpointName={endpointName} canSync justSaved />
        </div>
      )}

    </div>
  );
}

function PolicyForm({ form, errors, hasErrors, saving, scoped, patch, setForm, userOptions, hostOptions, instances, gateways, onCancel, onSave }) {
  const [showErrors, setShowErrors] = useState(false);
  const err = (k) => showErrors && errors[k] ? <div style={{ color: AN_COLORS.danger, fontSize: 12, marginTop: 4 }}>{errors[k]}</div> : null;
  // The client-side check first; failing that, what the last save answered.
  const sudoErr =
    err("sudo") ||
    (form.sudoServerError ? <div style={{ color: AN_COLORS.danger, fontSize: 12, marginTop: 4 }}>{form.sudoServerError}</div> : null);
  // Editing sudo clears the server's answer about it; the name doesn't follow it.
  const patchSudo = (changes) => setForm((f) => ({ ...f, ...changes, sudoServerError: null }));
  // A host typed as address:port still belongs to the endpoint at that address.
  const pickedEndpoints = hostOptions
    .filter((o) => form.hosts.some((h) => h === o.value || h.split(":")[0] === o.value))
    .map((o) => o.row);
  // The gateways the picked hosts are reached through that are disabled.
  const disabledGateways = gateways.filter(
    (g) =>
      String(g.status || "").toUpperCase() === "DISABLED" &&
      pickedEndpoints.some((r) => String(r.jumpServerId) === String(g.id)),
  );

  // Hosts that won't get grants: typed values that match no onboarded
  // endpoint, or match one with no gateway. The backend skips them silently,
  // so say it here.
  const hostProblems = form.hosts
    .map((h) => {
      const ep = endpointForHost(instances, h);
      if (!ep) return { host: h, why: "isn't an onboarded endpoint" };
      if (!ep.jumpServerId) return { host: h, why: "has no gateway assigned" };
      return null;
    })
    .filter(Boolean);
  const noKeyAccounts = form.accounts.filter(isNoKeyAccount);

  // The accounts scanned on the picked endpoints (their union), re-read when
  // the hosts change, and each endpoint's admin (onboarding sudo) account.
  // Typing a name still works: an endpoint may have accounts not yet scanned.
  const pickedIds = pickedEndpoints.map((r) => r.instanceId).filter(Boolean).sort((a, b) => a - b).join(",");
  const [accountSuggestions, setAccountSuggestions] = useState([]);
  const [adminAccounts, setAdminAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  useEffect(() => {
    const ids = pickedIds ? pickedIds.split(",").map(Number) : [];
    if (!ids.length) {
      setAccountSuggestions([]);
      setAdminAccounts([]);
      setAccountsLoading(false);
      return undefined;
    }
    let live = true;
    setAccountsLoading(true);
    const scanned = new Promise((resolve) =>
      listEndpointAccounts({
        instanceIds: ids,
        onSuccess: (rows) => {
          const names = [];
          (rows || []).forEach((r) => (r.accounts || []).forEach((a) => a?.name && !names.includes(a.name) && names.push(a.name)));
          resolve(names);
        },
        onFailure: () => resolve([]),
      }),
    );
    // One small read per host: an endpoint onboarded with an admin account
    // answers mode "admin" and its username.
    const admins = Promise.all(
      ids.map(
        (instanceId) =>
          new Promise((resolve) =>
            getAdminCredentialStatus({
              instanceId,
              onSuccess: (d) => resolve(d?.mode === "admin" && d?.username ? d.username : null),
              onFailure: () => resolve(null),
            }),
          ),
      ),
    );
    Promise.all([scanned, admins]).then(([names, adminNames]) => {
      if (!live) return;
      setAccountSuggestions(names);
      setAdminAccounts([...new Set(adminNames.filter(Boolean))]);
      setAccountsLoading(false);
    });
    return () => {
      live = false;
    };
  }, [pickedIds]);

  // Options: the scanned accounts (and admin accounts), then accounts this
  // policy names that the picked hosts no longer report (muted), then temp.
  const offered = [...new Set([...accountSuggestions, ...adminAccounts])].filter((n) => n !== TEMP_ACCOUNT);
  const notScanned = form.accounts.filter((a) => a !== TEMP_ACCOUNT && !offered.includes(a));
  const accountOptions = [
    ...offered.map((n) => ({
      value: n,
      label: (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontFamily: AN_MONO }}>{n}</span>
          {adminAccounts.includes(n) && <span style={ADMIN_BADGE}>Admin account</span>}
        </span>
      ),
      disabled: isNoKeyAccount(n),
      title: isNoKeyAccount(n) ? "Grants can't be issued for this account." : adminAccounts.includes(n) ? `${n} — the endpoint's admin account` : n,
    })),
    ...notScanned.map((n) => ({
      value: n,
      label: (
        <span style={{ color: AN_COLORS.muted }}>
          <span style={{ fontFamily: AN_MONO }}>{n}</span> · not found on the selected hosts
        </span>
      ),
      title: `${n} — not found on the selected hosts`,
    })),
    { value: TEMP_ACCOUNT, label: TEMP_LABEL, title: "Each session gets its own account, deleted when it ends." },
  ];

  // Laid out like the gateway and endpoint onboarding forms: numbered steps
  // in a dialog, so adding anything in PAM reads the same way.
  return (
    <div style={{ display: "grid", gap: 14, fontFamily: AN_FONT }}>
      <StepRow n={1} title="Who can connect" desc="Operators matched by the email on their SSO account.">
        <div style={{ display: "grid", gap: 12 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
              <FieldLabel required>Who</FieldLabel>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12.5, color: AN_COLORS.ink2 }}>
                <Switch size="small" checked={form.matchAll} onChange={(v) => patch({ matchAll: v })} />
                Everyone in the organisation
              </span>
            </div>
            <Select
              mode="multiple"
              value={form.matchAll ? [] : form.users}
              onChange={(v) => patch({ users: v })}
              options={userOptions}
              disabled={form.matchAll}
              placeholder={form.matchAll ? "Everyone in the organisation" : "Select users by email"}
              style={{ width: "100%" }}
              showSearch
              optionFilterProp="label"
            />
            {err("users")}
          </div>
          <div>
            <FieldLabel hint="not available yet — groups aren't resolved on the SSH path, so a group would match nobody">
              Groups <Users size={11} style={{ verticalAlign: -1 }} />
            </FieldLabel>
            <Select mode="multiple" value={[]} disabled={!GROUP_SCOPE_SUPPORTED} placeholder="Not available yet" style={{ width: "100%" }} />
          </div>
        </div>
      </StepRow>

      <StepRow
        n={2}
        title="Where"
        desc={scoped ? "Endpoints on this gateway, and the local accounts on them." : "Endpoints, and the local accounts on them. Each endpoint is reached through its own gateway."}
      >
        <div style={{ display: "grid", gap: 12 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <label>
              {/* Picked, not typed: a grant is issued only for a host that is an
                  onboarded endpoint with a gateway, and the policy, the grant
                  and the connect command all name it by the endpoint's address. */}
              <FieldLabel required hint={scoped ? "onboarded endpoints on this gateway" : "onboarded endpoints · gateway shown"}>Hosts</FieldLabel>
              <Select
                mode="multiple"
                value={form.hosts}
                onChange={(v) => patch({ hosts: v })}
                options={hostOptions.map(({ value, label }) => ({ value, label }))}
                placeholder={hostOptions.length ? "Pick endpoints" : "No onboarded endpoints with a gateway yet"}
                style={{ width: "100%" }}
                optionFilterProp="label"
              />
              {err("hosts")}
            </label>
            <label>
              <FieldLabel required hint={offered.length ? "scanned on the picked hosts · or type any" : undefined}>
                Accounts
              </FieldLabel>
              <Select
                mode="tags"
                value={form.accounts}
                onChange={(v) => patch({ accounts: [...new Set(v.map((a) => String(a).trim()).filter(Boolean))] })}
                options={accountOptions}
                optionFilterProp="value"
                loading={accountsLoading}
                // temp reads as what it is; an account the hosts no longer
                // report is muted.
                tagRender={({ value, closable, onClose }) =>
                  value === TEMP_ACCOUNT ? (
                    <Tag color="purple" closable={closable} onClose={onClose} style={{ marginInlineEnd: 4 }}>
                      {TEMP_LABEL}
                    </Tag>
                  ) : (
                    <Tag
                      closable={closable}
                      onClose={onClose}
                      style={{ marginInlineEnd: 4, fontFamily: AN_MONO, ...(notScanned.includes(value) && pickedIds ? { color: AN_COLORS.muted } : null) }}
                    >
                      {value}
                      {adminAccounts.includes(value) && <span style={{ ...ADMIN_BADGE, marginLeft: 6, fontSize: 9.5, padding: "0 4px" }}>Admin account</span>}
                    </Tag>
                  )
                }
                placeholder={offered.length ? `e.g. ${offered.slice(0, 2).join(", ")}` : "ubuntu, oracle…"}
                style={{ width: "100%" }}
                tokenSeparators={[",", " "]}
              />
              {err("accounts")}
            </label>
          </div>
          <div style={{ fontSize: 12, color: AN_COLORS.muted, lineHeight: 1.5 }}>
            Each account you name gets its own grant on each host you pick.
          </div>
        </div>
      </StepRow>

      <StepRow n={3} title="Sudo" desc="Elevate these sessions for a limited time, on top of the account's own rights.">
        <div style={{ display: "grid", gap: 12 }}>
          <div>
            <Radio.Group value={form.sudoMode} onChange={(e) => patchSudo({ sudoMode: e.target.value })}>
              {SUDO_MODES.map((m) => (
                <Radio key={m.value} value={m.value}>
                  {m.label}
                </Radio>
              ))}
            </Radio.Group>
          </div>
          {form.sudoMode === "commands" && (
            <label>
              <FieldLabel required hint="one absolute path per line, optionally with arguments">Commands</FieldLabel>
              <Input.TextArea
                value={form.sudoCommands}
                onChange={(e) => patchSudo({ sudoCommands: e.target.value })}
                autoSize={{ minRows: 3, maxRows: 10 }}
                placeholder={"/usr/bin/systemctl restart nginx\n/usr/bin/journalctl"}
                style={{ fontFamily: AN_MONO, fontSize: 12.5 }}
                spellCheck={false}
              />
              <div style={{ fontSize: 12, color: AN_COLORS.muted, marginTop: 4 }}>
                e.g. <InlineValue>/usr/bin/systemctl restart nginx</InlineValue>. A bare path allows any arguments.
              </div>
            </label>
          )}
          {form.sudoMode !== "none" && (
            <div>
              <FieldLabel required>Time limit</FieldLabel>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12.5, color: AN_COLORS.ink2 }}>
                <InputNumber
                  min={SUDO_MIN_MINUTES}
                  max={SUDO_MAX_MINUTES}
                  precision={0}
                  value={form.sudoMinutes}
                  onChange={(v) => patchSudo({ sudoMinutes: v ?? null })}
                  style={{ width: 100 }}
                />
                minutes
              </span>
              <div style={{ fontSize: 12, color: AN_COLORS.muted, marginTop: 6 }}>
                Applied only to sessions approved with MFA. Removed when the session ends.
              </div>
              {sudoErr}
            </div>
          )}
          {form.sudoMode !== "none" && form.policyFlow !== "mfa_required" && (
            <Note tone="warn" icon={<AlertTriangle size={15} />}>
              {form.policyFlow === "block"
                ? "This policy blocks its sessions, so this sudo never applies."
                : "Sessions this policy lets in without MFA are not elevated — set the Action to require MFA for this sudo to apply."}
            </Note>
          )}
        </div>
      </StepRow>

      <StepRow n={4} title="Action" desc="What happens when one of these sessions opens.">
        <div>
          <Radio.Group value={form.policyFlow} onChange={(e) => patch({ policyFlow: e.target.value })}>
            {SSH_POLICY_FLOWS.map((f) => {
              const locked = f.adminOnly && !isAdminUser() && form.policyFlow !== f.value;
              return (
                <Radio key={f.value} value={f.value} disabled={locked}>
                  <span title={locked ? "Administrators only" : undefined}>
                    {f.label}
                    {f.value === DEFAULT_SSH_POLICY_FLOW ? " (default)" : ""}
                  </span>
                </Radio>
              );
            })}
          </Radio.Group>
          <div style={{ fontSize: 12, color: AN_COLORS.muted, marginTop: 6 }}>
            Deny always wins: if any matching policy denies, the session is refused, whatever else matches.
          </div>
        </div>
      </StepRow>

      <StepRow n={5} title="Name" desc="Filled in from the choices above until you edit it." last>
        <label>
          <Input value={form.policyName} onChange={(e) => setForm((f) => ({ ...f, policyName: e.target.value, nameTouched: true }))} />
          {err("policyName")}
        </label>
      </StepRow>

      {/* Inline warnings */}
      <div style={{ display: "grid", gap: 9 }}>
        {hostProblems.length > 0 && (
          <Note tone="warn" icon={<AlertTriangle size={15} />}>
            No grants will be issued for:{" "}
            {hostProblems.map((p, i) => (
              <React.Fragment key={p.host}>
                {i > 0 && "; "}
                <InlineValue>{p.host}</InlineValue> {p.why}
              </React.Fragment>
            ))}
            . Pick hosts from the endpoint list.
          </Note>
        )}
        {noKeyAccounts.length > 0 && (
          <Note tone="warn" icon={<AlertTriangle size={15} />}>
            {noKeyAccounts.map((a, i) => (
              <React.Fragment key={a}>
                {i > 0 && ", "}
                <InlineValue>{a}</InlineValue>
              </React.Fragment>
            ))}{" "}
            never {noKeyAccounts.length === 1 ? "gets a key" : "get keys"}, so sessions to {noKeyAccounts.length === 1 ? "it" : "them"} will be refused.
          </Note>
        )}
        {form.policyFlow === "allow" && (
          <Note tone="danger" icon={<AlertTriangle size={15} />}>
            <strong>No second factor.</strong> Sessions open without an approval push.
          </Note>
        )}
        {disabledGateways.map((g) => (
          <Note key={g.id} tone="danger" icon={<AlertTriangle size={15} />}>
            {g.serverName || "A gateway"} is disabled, so no session to the hosts behind it can open until it's enabled again.
          </Note>
        ))}
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          type="primary"
          loading={saving}
          onClick={() => {
            setShowErrors(true);
            if (!hasErrors) onSave();
          }}
        >
          {form.id ? "Save changes" : "Save policy"}
        </Button>
      </div>
    </div>
  );
}

// "What did I just grant?" — listConnections per selected user, commands
// rendered exactly as the server returned them.
function GrantedPanel({ granted, onClose }) {
  return (
    <div style={{ ...card, padding: 18 }}>
      <PanelHead
        eyebrow="Granted"
        title="What this policy grants"
        desc={
          granted.matchAll
            ? "Every user in the organisation gets connect commands in this format. Shown for your own account."
            : "The connect commands each user now has, through each host's gateway."
        }
        right={<Button size="small" onClick={onClose}>Close</Button>}
      />
      {granted.flow === "block" && (
        <div style={{ marginBottom: 12 }}>
          <Note tone="info">This policy blocks these sessions, so it grants no connect commands.</Note>
        </div>
      )}
      <div style={{ display: "grid", gap: 12 }}>
        {granted.rows.map((r) => (
          <div key={r.email} style={{ border: `1px solid ${AN_COLORS.line2}`, borderRadius: 8, padding: "10px 12px" }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: AN_COLORS.ink, marginBottom: 6, fontFamily: AN_FONT }}>{r.email}</div>
            {r.loading ? (
              <Spin size="small" />
            ) : r.error ? (
              <div style={{ fontSize: 12.5, color: AN_COLORS.danger }}>{r.error}</div>
            ) : (r.connections || []).length === 0 ? (
              <div style={{ fontSize: 12.5, color: AN_COLORS.muted }}>{r.message || "No connect commands yet."}</div>
            ) : (
              <div style={{ display: "grid", gap: 6 }}>
                {r.connections.map((c) => (
                  <div key={`${c.host}|${c.account}|${c.command}`} style={{ display: "grid", gridTemplateColumns: "minmax(110px,auto) minmax(70px,auto) 1fr auto", gap: 10, alignItems: "center", fontSize: 12.5 }}>
                    <span style={{ fontFamily: AN_MONO }}>{c.host}</span>
                    <span style={{ fontFamily: AN_MONO, color: AN_COLORS.ink3 }}>{c.account}</span>
                    <span style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0, fontFamily: AN_MONO, background: AN_COLORS.tint, borderRadius: 6, padding: "4px 8px", overflowX: "auto", whiteSpace: "nowrap" }}>
                      <Terminal size={12} style={{ flexShrink: 0, color: AN_COLORS.muted }} />
                      {c.command}
                    </span>
                    <CopyButton text={c.command} label="Copy" />
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
